import Image from 'next/image';
import type { ComponentProps } from 'react';

import type { SanityImageWithMeta } from '@/sanity/image';

type NextImageProps = ComponentProps<typeof Image>;

interface SanityImageProps extends Omit<NextImageProps, 'src' | 'alt' | 'width' | 'height'> {
  image: SanityImageWithMeta | null | undefined;
  fallbackSrc?: string;
  fallbackAlt?: string;
  alt?: string;
  width?: number;
  height?: number;
}

/**
 * Renders a Sanity-hosted image with graceful fallback to a /public asset.
 * Resolves alt text from (explicit prop) → (Sanity alt) → (fallback alt).
 * Supports raster and SVG because next/image has `dangerouslyAllowSVG: true`.
 */
export function SanityImage({
  image,
  fallbackSrc,
  fallbackAlt = '',
  alt,
  width,
  height,
  fill,
  ...rest
}: SanityImageProps) {
  const src = image?.url ?? fallbackSrc;
  if (!src) return null;

  const effectiveAlt = alt ?? image?.alt ?? fallbackAlt;

  const blurProps = image?.lqip
    ? ({ placeholder: 'blur' as const, blurDataURL: image.lqip })
    : {};

  if (fill) {
    return (
      <Image src={src} alt={effectiveAlt} fill {...blurProps} {...rest} />
    );
  }

  const w = width ?? image?.width ?? 0;
  const h = height ?? image?.height ?? 0;

  return (
    <Image
      src={src}
      alt={effectiveAlt}
      width={w}
      height={h}
      {...blurProps}
      {...rest}
    />
  );
}
